/**
 * Collapses an MFA step-up rejection into the two causes the UI is allowed to
 * name, for the same reason the sign-in path has one: the provider's own
 * `error.message` is untranslated, changes with the backend version, and is
 * written for an operator rather than for the person holding the phone.
 *
 * Unlike the sign-in classifier this makes no enumeration promise - the caller
 * is already authenticated to aal1 by the time a code is entered - so the split
 * is purely "your code" versus "our problem", and the default is the actionable
 * one. `verifyMFACode` hands back a bare string, so the system side is
 * recognised by substring scan; a plain `includes` never backtracks.
 */

export type MfaVerifyErrorKey = 'invalidCode' | 'generic';

/** i18n keys in the `auth` namespace, one per classified cause. */
export const MFA_VERIFY_ERROR_KEYS = {
  invalidCode: 'mfaVerify.invalidCode',
  generic: 'mfaVerify.genericError',
} as const satisfies Record<MfaVerifyErrorKey, string>;

/**
 * Lower-cased fragments that mean the request never reached a verdict on the
 * code. Everything else is treated as a rejected code, because that is both the
 * dominant case and the only one the user can act on.
 */
const SYSTEM_FAILURE_FRAGMENTS = [
  'failed to fetch',
  'load failed',
  'networkerror',
  'network request failed',
  'rate limit',
  'too many',
  'timeout',
  'unavailable',
];

function readMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error === 'object' && error !== null) {
    const { message } = error as { message?: unknown };
    return typeof message === 'string' ? message : '';
  }
  return '';
}

export function classifyMfaVerifyError(error: unknown): MfaVerifyErrorKey {
  const lowered = readMessage(error).toLowerCase();
  return SYSTEM_FAILURE_FRAGMENTS.some((fragment) => lowered.includes(fragment))
    ? 'generic'
    : 'invalidCode';
}
